import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Switch } from '../components/ui/switch';
import { MoneyInput } from '../components/MoneyInput';
import { toast } from 'sonner';
import { Download, Trash2, ChevronLeft, ChevronRight, Pencil, CalendarIcon, Save, Search, Upload, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';

export default function SalesRecordsPage() {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [locations, setLocations] = useState([]);
  const [dropdowns, setDropdowns] = useState({});
  const [filters, setFilters] = useState({ location_id: '', date_from: '', date_to: '', search: '' });
  const [editRecord, setEditRecord] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [reviewFilter, setReviewFilter] = useState('all'); // 'all' | 'needs_review' | 'fully_paid' | 'outstanding'
  const [payDrawer, setPayDrawer] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [calStates, setCalStates] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importChanges, setImportChanges] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const limit = 25;

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filters.location_id) params.location_id = filters.location_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await axios.get(`${API}/sales`, { params });
      let recs = res.data.records;
      // Client-side patient name search
      if (filters.search.trim()) {
        const q = filters.search.trim().toLowerCase();
        recs = recs.filter(r =>
          `${r.patient_first_name} ${r.patient_last_name}`.toLowerCase().includes(q) ||
          (r.treatment_sold || '').toLowerCase().includes(q) ||
          (r.referral_source || '').toLowerCase().includes(q)
        );
      }
      if (reviewFilter === 'needs_review') recs = recs.filter(r => r.needs_review);
      if (reviewFilter === 'fully_paid') recs = recs.filter(r => r.fully_paid);
      if (reviewFilter === 'outstanding') recs = recs.filter(r => !r.fully_paid);
      setRecords(recs);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load records'); }
    finally { setLoading(false); }
  }, [page, filters, reviewFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchRecords(); }, [fetchRecords]);
  useEffect(() => {
    axios.get(`${API}/locations`).then(r => setLocations(r.data)).catch(() => {});
    axios.get(`${API}/dropdowns`).then(r => setDropdowns(r.data)).catch(() => {});
  }, []);

  const handleExport = () => {
    const params = new URLSearchParams();
    if (filters.location_id) params.append('location_id', filters.location_id);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    const token = localStorage.getItem('arch_token'); // SPA auth token - acceptable for authenticated downloads
    fetch(`${API}/export/sales?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
        a.download = 'sales_records.csv'; a.click();
      });
  };

  const setCal = (name, open) => setCalStates(prev => ({ ...prev, [name]: open }));

  const openEdit = (r) => {
    setEditForm({
      submission_datetime: r.submission_datetime || new Date().toISOString(),
      location_id: r.location_id || '',
      patient_first_name: r.patient_first_name || '',
      patient_last_name: r.patient_last_name || '',
      initial_consult_date: r.initial_consult_date ? r.initial_consult_date.slice(0,10) : '',
      treatment_sold: r.treatment_sold || '',
      total_sales_amount: r.total_sales_amount || '',
      total_collected_today: r.total_collected_today || '',
      payment_method: r.payment_method || '',
      financing_amount: r.financing_amount || '',
      other_funds_amount: r.other_funds_amount || '',
      anticipated_paid_in_full_date: r.anticipated_paid_in_full_date ? r.anticipated_paid_in_full_date.slice(0,10) : '',
      treatment_start_date: r.treatment_start_date ? r.treatment_start_date.slice(0,10) : '',
      referral_source: r.referral_source || '',
      will_use_dental_insurance: r.will_use_dental_insurance || false,
      notes: r.notes || '',
      closed_by: r.closed_by || ''
    });
    setEditRecord(r);
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.location_id) params.location_id = filters.location_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      
      const response = await axios.get(`${API}/export/sales-excel`, {
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sales_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Excel file downloaded! Marketing Source column has dropdown for easy editing.');
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };
  
  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/import/sales-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportChanges(res.data.changes);
      setShowImportDialog(true);
      toast.success(`Updated ${res.data.updated_count} sales records`);
      fetchRecords();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const setEF = (name, val) => setEditForm(prev => ({ ...prev, [name]: val }));

  const DatePicker = ({ fieldName, label }) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Popover open={calStates[fieldName]} onOpenChange={o => setCal(fieldName, o)}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-start text-xs h-8 bg-white">
            <CalendarIcon size={11} className="mr-1.5" />
            {editForm[fieldName] ? format(new Date(editForm[fieldName] + 'T00:00:00'), 'MMM d, yyyy') : 'Select'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white">
          <Calendar mode="single"
            selected={editForm[fieldName] ? new Date(editForm[fieldName] + 'T00:00:00') : undefined}
            onSelect={d => { if (d) { setEF(fieldName, format(d, 'yyyy-MM-dd')); setCal(fieldName, false); } }} />
        </PopoverContent>
      </Popover>
    </div>
  );

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = {
        ...editForm,
        total_sales_amount: parseFloat(editForm.total_sales_amount) || 0,
        total_collected_today: parseFloat(editForm.total_collected_today) || 0,
        financing_amount: parseFloat(editForm.financing_amount) || 0,
        other_funds_amount: parseFloat(editForm.other_funds_amount) || 0,
        anticipated_paid_in_full_date: editForm.anticipated_paid_in_full_date || null,
        treatment_start_date: editForm.treatment_start_date || null,
      };
      await axios.put(`${API}/sales/${editRecord.id}`, payload);
      toast.success('Sale record updated');
      setEditRecord(null);
      fetchRecords();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to update'); }
    finally { setSavingEdit(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sale record?')) return;
    try { await axios.delete(`${API}/sales/${id}`); toast.success('Deleted'); fetchRecords(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed to delete'); }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Sales Records</h1>
          <p className="text-slate-500 text-sm mt-1">{total} total records</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExport} className="gap-2 bg-white text-slate-800 border border-slate-200 hover:bg-slate-50 h-8 text-xs" data-testid="sales-table-export-button">
            <Download size={13} /> Export CSV
          </Button>
          <Button onClick={handleExportExcel} disabled={exporting} className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs">
            {exporting ? 'Exporting...' : <><Download size={13} /> Export Excel</>}
          </Button>
          <label>
            <input type="file" accept=".xlsx,.xls" onChange={handleImportExcel} className="hidden" disabled={importing} />
            <Button as="span" disabled={importing} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs cursor-pointer">
              {importing ? 'Importing...' : <><Upload size={13} /> Import Excel</>}
            </Button>
          </label>
        </div>
      </div>
      <Card className="bg-white shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                placeholder="Search patient, treatment..." className="h-8 text-xs pl-8 w-52 bg-white" />
            </div>
            <Select value={filters.location_id || 'all'} onValueChange={v => setFilters(f => ({ ...f, location_id: v === 'all' ? '' : v }))}>
              <SelectTrigger className="h-8 text-xs w-40 bg-white"><SelectValue placeholder="All Locations" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} className="h-8 text-xs w-36 bg-white" />
            <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} className="h-8 text-xs w-36 bg-white" />
            <Select value={reviewFilter} onValueChange={setReviewFilter}>
              <SelectTrigger className="h-8 text-xs w-44 bg-white" data-testid="sales-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All records</SelectItem>
                <SelectItem value="needs_review">Needs review</SelectItem>
                <SelectItem value="outstanding">Outstanding balance</SelectItem>
                <SelectItem value="fully_paid">Fully paid</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" onClick={() => { setFilters({ location_id: '', date_from: '', date_to: '', search: '' }); setReviewFilter('all'); }} variant="ghost" className="h-8 text-xs">Clear</Button>
          </div>
        </CardContent>
      </Card>
      <Card className="bg-white shadow-sm">
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-100">
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Date</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Patient</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">TC</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Sold</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Balance</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Referral</TableHead>
                  <TableHead className="w-28"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? ([...Array(5)].map((_, i) => (
                  <TableRow key={i}>{[...Array(8)].map((_, j) => <TableCell key={j}><Skeleton className="h-4" /></TableCell>)}</TableRow>
                ))) : records.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="py-12 text-center text-slate-400">No records found</TableCell></TableRow>
                ) : (
                  records.map(r => {
                    const sold = Number(r.total_sales_amount || 0);
                    const initial = Number(r.total_collected_today || 0);
                    const balance = Math.max(sold - initial, 0); // A/R subsequent payments not in this list — fetched separately when needed
                    const isPaid = r.fully_paid || balance <= 0.01;
                    return (
                      <TableRow key={r.id} className="data-row border-slate-50">
                        <TableCell className="text-xs text-slate-600">{r.submission_datetime ? format(new Date(r.submission_datetime), 'MMM d, yyyy') : '-'}</TableCell>
                        <TableCell className="text-xs font-medium text-slate-800">
                          <div className="flex items-center gap-1.5">
                            <span>{r.patient_first_name} {r.patient_last_name}</span>
                            {r.needs_review && (
                              <Badge className="text-[9px] py-0 px-1.5 font-semibold" style={{ background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D' }} data-testid={`needs-review-${r.id}`}>
                                <AlertCircle size={9} className="mr-0.5" /> NEEDS REVIEW
                              </Badge>
                            )}
                          </div>
                          <div className="text-[10px] text-slate-400 mt-0.5">{r.treatment_sold}{r.arches_count > 0 ? ` · ${r.arches_count} arch${r.arches_count > 1 ? 'es' : ''}` : ''}</div>
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">{r.treatment_coordinator || r.closed_by || '—'}</TableCell>
                        <TableCell className="text-xs font-semibold text-slate-800">${sold.toLocaleString()}</TableCell>
                        <TableCell className="text-xs font-semibold" style={{ color: isPaid ? '#15803D' : '#9F1239' }}>
                          {isPaid ? '$0' : `$${balance.toLocaleString()}`}
                        </TableCell>
                        <TableCell className="text-xs">
                          {isPaid ? (
                            <Badge style={{ background: '#DCFCE7', color: '#15803D', border: '1px solid #86EFAC' }} className="text-[10px]">FULLY PAID</Badge>
                          ) : initial > 0 ? (
                            <Badge style={{ background: 'rgba(201,169,97,0.15)', color: '#8A6A2A', border: '1px solid rgba(201,169,97,0.4)' }} className="text-[10px]">SOLD · PARTIAL</Badge>
                          ) : (
                            <Badge style={{ background: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB' }} className="text-[10px]">PRESENTED</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">{r.referral_source || <span className="text-slate-400">—</span>}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {!isPaid && (
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px] hover:bg-green-50 hover:text-green-700" onClick={() => { setPayDrawer(r); setPayAmount(String(balance.toFixed(2))); }} data-testid={`pay-btn-${r.id}`}>
                                <Save size={11} className="mr-1" /> Pay
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:text-cyan-600" onClick={() => openEdit(r)}><Pencil size={12} /></Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:text-red-500" onClick={() => handleDelete(r.id)}><Trash2 size={12} /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </ScrollArea>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={13} /></Button>
                <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={13} /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Pay drawer */}
      <Dialog open={!!payDrawer} onOpenChange={o => !o && setPayDrawer(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="quick-pay-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Update Payment</DialogTitle>
          </DialogHeader>
          {payDrawer && (
            <div className="space-y-4">
              <div className="rounded-md p-3 border bg-stone-50">
                <p className="text-sm font-medium">{payDrawer.patient_first_name} {payDrawer.patient_last_name}</p>
                <p className="text-xs text-stone-500 mt-0.5">Sold ${Number(payDrawer.total_sales_amount || 0).toLocaleString()} · Collected at sale ${Number(payDrawer.total_collected_today || 0).toLocaleString()}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Additional payment amount</Label>
                <Input type="number" min="0" step="0.01" value={payAmount} onChange={e => setPayAmount(e.target.value)} data-testid="quick-pay-amount" />
                <p className="text-[10px] text-stone-500">Outstanding shown is initial-only; full A/R history on the A/R page.</p>
              </div>
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  style={{ background: '#0E0E0E', color: '#C9A961' }}
                  data-testid="quick-pay-add-btn"
                  onClick={async () => {
                    const amt = parseFloat(payAmount);
                    if (!amt || amt <= 0) { toast.error('Enter a positive amount'); return; }
                    try {
                      await axios.patch(`${API}/sales/${payDrawer.id}/quick`, { additional_payment: amt });
                      toast.success(`Payment of $${amt.toLocaleString()} recorded`);
                      setPayDrawer(null); setPayAmount('');
                      fetchRecords();
                    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
                  }}
                >
                  <Save size={13} className="mr-1.5" /> Add payment
                </Button>
                <Button
                  variant="outline"
                  data-testid="quick-pay-fully-paid-btn"
                  onClick={async () => {
                    try {
                      await axios.patch(`${API}/sales/${payDrawer.id}/quick`, { fully_paid: true });
                      toast.success('Marked as fully paid');
                      setPayDrawer(null); setPayAmount('');
                      fetchRecords();
                    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
                  }}
                >
                  Mark fully paid
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Edit Sale Dialog */}
      <Dialog open={!!editRecord} onOpenChange={o => !o && setEditRecord(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Sale Record</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Location</Label>
                <Select value={editForm.location_id} onValueChange={v => setEF('location_id', v)}>
                  <SelectTrigger className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {locations.filter(l => l.active).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <DatePicker fieldName="initial_consult_date" label="Consult Date" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">First Name *</Label>
                <Input value={editForm.patient_first_name} onChange={e => setEF('patient_first_name', e.target.value)} className="h-8 text-xs bg-white" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Last Name *</Label>
                <Input value={editForm.patient_last_name} onChange={e => setEF('patient_last_name', e.target.value)} className="h-8 text-xs bg-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Treatment Sold</Label>
                <Select value={editForm.treatment_sold} onValueChange={v => setEF('treatment_sold', v)}>
                  <SelectTrigger className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {(dropdowns.treatment_types || []).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Payment Method</Label>
                <Select value={editForm.payment_method} onValueChange={v => setEF('payment_method', v)}>
                  <SelectTrigger className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {(dropdowns.payment_methods || []).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Total Sale Amount</Label>
                <MoneyInput value={editForm.total_sales_amount} onValueChange={v => setEF('total_sales_amount', v)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Collected Today</Label>
                <MoneyInput value={editForm.total_collected_today} onValueChange={v => setEF('total_collected_today', v)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Financing Amount</Label>
                <MoneyInput value={editForm.financing_amount} onValueChange={v => setEF('financing_amount', v)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Referral Source</Label>
                <Select value={editForm.referral_source} onValueChange={v => setEF('referral_source', v)}>
                  <SelectTrigger className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {(dropdowns.referral_sources || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <DatePicker fieldName="treatment_start_date" label="Treatment Start Date" />
              <DatePicker fieldName="anticipated_paid_in_full_date" label="Paid In Full Date" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={editForm.will_use_dental_insurance} onCheckedChange={v => setEF('will_use_dental_insurance', v)} />
              <Label className="text-xs">Will Use Dental Insurance</Label>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Textarea value={editForm.notes} onChange={e => setEF('notes', e.target.value)} className="bg-white text-xs min-h-[60px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="bg-white" onClick={() => setEditRecord(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit}
              style={{ background: 'hsl(191,95%,43%)', color: 'hsl(222,47%,11%)' }}>
              <Save size={13} className="mr-1.5" />{savingEdit ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Changes Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="text-amber-600" size={20} />
              Marketing Source Changes ({importChanges?.length || 0})
            </DialogTitle>
          </DialogHeader>
          {importChanges && importChanges.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm text-slate-600">The following sales had their marketing source updated:</p>
              <div className="max-h-96 overflow-auto border rounded">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Patient</TableHead>
                      <TableHead>Old Source</TableHead>
                      <TableHead>→</TableHead>
                      <TableHead>New Source</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importChanges.map((change, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{change.patient_name}</TableCell>
                        <TableCell><Badge variant="outline" className="bg-red-50">{change.old_source || '—'}</Badge></TableCell>
                        <TableCell>→</TableCell>
                        <TableCell><Badge className="bg-green-100 text-green-800">{change.new_source}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-600">No changes detected.</p>
          )}
          <DialogFooter>
            <Button onClick={() => setShowImportDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
